import { useEffect, useState } from 'react';
import { Form, Input } from 'antd';
import styled from 'styled-components';
import discount_delete_icon from '../v2-icons/discount-delete.svg';
import Icon from 'assets/Icons';
import useGetListMailCPA from 'features/settingService/services/hooks/useGetListMailCPA';
import { setLoadingPage } from 'services/UI/sagas';
import settingApis from 'features/settingService/services/apis';
import toast from 'utils/toast';

type FormType = {
  emails: { email: string }[];
};

const SendEmailCPA = () => {
  const [listMailCPA] = useGetListMailCPA();
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState<FormType>({
    emails: [
      {
        email: '',
      },
    ],
  });

  const onReset = () => {
    form.resetFields();
  };

  const onDone = () => {
    form.submit();
  };

  const onFinish = async (values: FormType) => {
    try {
      setLoadingPage(true);
      const data = values.emails.map((email) => email.email);
      const res = await settingApis.updateListMailCPA(data);
      if (res.data.data) {
        toast.success('List mail CPA updated successfully');
      } else {
        toast.error('Failed to update list mail CPA');
      }
    } catch (error) {
      toast.error('Failed to update list mail CPA');
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const listEmails = listMailCPA?.map((email) => ({ email }));
    setInitialValues({ emails: listEmails });
    form.setFieldValue('emails', listEmails);
  }, [listMailCPA]);

  return (
    <Wrrapper>
      <Container>
        <Form<FormType> form={form} layout='vertical' onFinish={onFinish} initialValues={initialValues}>
          <ListContainer>
            <Form.List name={'emails'}>
              {(fields, { remove, add }) => (
                <>
                  <ItemsContainer>
                    {fields.map(({ key, name }) => (
                      <Item key={key}>
                        <Form.Item
                          label='Email'
                          style={{ flex: 1 }}
                          name={[name, 'email']}
                          rules={[
                            { required: true, message: 'Please enter email!' },
                            { type: 'email', message: 'Please enter valid email!' },
                          ]}
                        >
                          <Input
                            placeholder='Email'
                            style={{ height: '3.5rem' }}
                            allowClear={{ clearIcon: <OutlineCloseIcon /> }}
                          />
                        </Form.Item>
                        {fields.length > 1 && (
                          <RemoveEmailButton type='button' onClick={() => remove(name)}>
                            <img
                              src={discount_delete_icon}
                              alt='discount_delete_icon'
                              style={{ width: 32, height: 32 }}
                            />
                          </RemoveEmailButton>
                        )}
                      </Item>
                    ))}
                  </ItemsContainer>
                  <AddEmailButton type='button' onClick={() => add()}>
                    <div className='add-email-content'>
                      <div className='icon'>
                        <Icon type='addWhite' width={24} height={24} />
                      </div>
                      <div className='text'>Add Email</div>
                    </div>
                  </AddEmailButton>
                </>
              )}
            </Form.List>
          </ListContainer>
        </Form>
      </Container>
      <StickyFooter>
        <ButtonFooter className='btn-reset' type='button' onClick={onReset}>
          Reset
        </ButtonFooter>
        <ButtonFooter className='btn-save' type='button' onClick={onDone}>
          Save
        </ButtonFooter>
      </StickyFooter>
    </Wrrapper>
  );
};

export default SendEmailCPA;

const Wrrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  background-color: #fff;
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label label {
    color: #1d2129;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  border-radius: 4px;
  border: 1px solid #ccd4dc;
  background: #f6f7fc;
`;

const StickyFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
  background: var(--Neutral-03, #f5f5f5);
  box-shadow: 0 -4px 8px 0 rgba(0, 0, 0, 0.15);
  padding: 16px;
`;
const ButtonFooter = styled.button`
  display: flex;
  width: 208px;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #e5e6eb;
  box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4), 0px 4px 8px -3px rgba(45, 35, 66, 0.3),
    0px -3px 0px 0px #d6d6e7 inset;
  color: #1d2129;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  &.btn-save {
    background: #ff8890;
    box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4), 0px 4px 8px -3px rgba(45, 35, 66, 0.3),
      0px -4px 0px 0px #e7646d inset;
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #ccd4dc;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  .ant-form-item {
    margin-bottom: 24px;
  }
  .ant-input-clear-icon {
    display: flex;
  }
`;

const AddEmailButton = styled.button`
  display: flex;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: var(--Secondary-02, #6fabb6);
  box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -4px 0 0 #144b55 inset;
  .add-email-content {
    display: flex;
    align-items: center;
    gap: 8px;
    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      align-self: stretch;
    }
    .text {
      color: #fff;
      text-align: center;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
  transition: all 0.3s ease;
  width: fit-content;
  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }
`;

const RemoveEmailButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
  margin-top: 8px;
`;

const OutlineCloseIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M15.0667 3.45996C15.4572 3.06957 16.0902 3.06948 16.4807 3.45996C16.8706 3.85049 16.8709 4.48368 16.4807 4.87402L11.384 9.96973L16.4807 15.0664C16.8707 15.457 16.8711 16.0901 16.4807 16.4805C16.0903 16.8707 15.4572 16.8704 15.0667 16.4805L9.96997 11.3838L4.87427 16.4805C4.48393 16.8707 3.85074 16.8704 3.46021 16.4805C3.06972 16.09 3.06979 15.4569 3.46021 15.0664L8.55591 9.96973L3.46021 4.87402C3.06985 4.48355 3.06988 3.85046 3.46021 3.45996C3.85069 3.06947 4.48373 3.06955 4.87427 3.45996L9.96997 8.55566L15.0667 3.45996Z'
      fill='#64748B'
    />
  </svg>
);
